<template>
  <div class="editor">
    <div class="head">
      <div class="meta">
        <span v-if="current && current.updated_by" class="meta-line">
          <span class="meta-label">Последняя правка</span>
          <span class="meta-author">{{ current.updated_by.username || current.updated_by.email }}</span>
          <span v-if="current.created_at" class="meta-date">· {{ formatDate(current.created_at) }}</span>
        </span>
        <span v-else class="meta-empty">Промпт ещё не задан</span>
      </div>
      <div class="actions">
        <button
            v-if="canEdit"
            class="save-btn"
            :disabled="saving || !changed || !valid"
            @click="onSave"
        >
          <b-spinner v-if="saving" small/>
          <span v-else>Сохранить</span>
        </button>
      </div>
    </div>

    <div class="editor-area">
      <textarea
          v-model="text"
          class="prompt-textarea"
          :readonly="!canEdit"
          :placeholder="canEdit ? 'Опишите термины и правила...' : 'Только для чтения'"
          spellcheck="false"
      />
    </div>

    <div class="footer">
      <div class="counter" :class="{ 'is-bad': !valid }">
        {{ text.length }} / {{ maxLength }}
      </div>
      <div v-if="errors.length" class="errors">
        <div v-for="(e, i) in errors" :key="i">{{ e }}</div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BusinessPromptEditor',
  props: {
    current: { type: Object, default: null },
    canEdit: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    serverErrors: { type: Array, default: () => [] },
  },
  data() {
    return {
      text: '',
      maxLength: 50000,
    };
  },
  computed: {
    valid() {
      return this.text.length > 0 && this.text.length <= this.maxLength;
    },
    changed() {
      return this.text !== ((this.current && this.current.text) || '');
    },
    errors() {
      const errs = [...this.serverErrors];
      if (this.text.length > this.maxLength) {
        errs.push(`Превышен максимум: ${this.maxLength} символов.`);
      }
      return errs;
    },
  },
  watch: {
    current: {
      immediate: true,
      handler(v) {
        this.text = (v && v.text) || '';
      },
    },
  },
  methods: {
    onSave() {
      if (!this.valid || !this.changed) return;
      if (!window.confirm('Это станет текущей версией промпта. Продолжить?')) return;
      this.$emit('save', this.text);
    },
    formatDate(iso) {
      try {
        return new Date(iso).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
      } catch (e) {
        return iso;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.editor {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 22px 28px 18px;
}

.head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  gap: 16px;
}

.meta {
  font-size: 13px;
  color: var(--ai-text-muted);
  min-width: 0;
}
.meta-line {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
}
.meta-label {
  text-transform: uppercase;
  letter-spacing: .04em;
  font-size: 11px;
  font-weight: 600;
  color: var(--ai-text-subtle);
}
.meta-author {
  font-weight: 500;
  color: var(--ai-text);
}
.meta-date { color: var(--ai-text-muted); }
.meta-empty { font-style: italic; }

.save-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 110px;
  padding: 8px 18px;
  background: var(--ai-accent);
  color: var(--ai-accent-fg);
  border: none;
  border-radius: 999px;
  font-size: 13.5px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity .15s ease;

  &:hover:not(:disabled) { opacity: .85; }
  &:disabled {
    background: #d9d9e3;
    cursor: default;
  }
}

.editor-area {
  flex: 1;
  min-height: 0;
  display: flex;
}

.prompt-textarea {
  flex: 1;
  width: 100%;
  resize: none;
  padding: 16px 18px;
  border: 1px solid var(--ai-border);
  border-radius: 14px;
  background: var(--ai-bg);
  font-family: var(--ai-font-mono);
  font-size: 13px;
  line-height: 1.6;
  color: var(--ai-text);
  outline: none;
  white-space: pre;
  tab-size: 2;
  transition: border-color .15s ease, box-shadow .15s ease;

  &:focus {
    border-color: #c5c5d2;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  }
  &:read-only {
    background: var(--ai-sidebar);
    color: var(--ai-text-muted);
  }
  &::placeholder { color: var(--ai-text-subtle); }
}

.footer {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-top: 10px;
  gap: 16px;
}
.counter {
  font-size: 11.5px;
  color: var(--ai-text-muted);
  &.is-bad { color: #b91c1c; font-weight: 600; }
}
.errors {
  font-size: 12.5px;
  color: #b91c1c;
  text-align: right;
  max-width: 60%;

  > div + div { margin-top: 2px; }
}
</style>
